/* eslint-disable promise/prefer-await-to-then */
import {
	DetailsList,
	DetailsListLayoutMode,
	Dropdown,
	IColumn,
	IDetailsGroupRenderProps,
	IDetailsListStyles,
	MessageBar,
	MessageBarType,
	ProgressIndicator,
	SelectionMode,
	Stack
} from "@fluentui/react"
import { useState } from "react"
import { useApiCall } from "../../hooks/apiHooks"

export function YearOverYearReport() {
	const xrm = (window.top as any).Xrm
	const userRoles = xrm.Utility.getGlobalContext().userSettings.roles.getAll()
	const allowedRoles = ["System Administrator", "CEO-Business Manager"] //TODO: Update roles as needed
	const isUserInRole = userRoles.some(role => allowedRoles.includes(role.name))

	const queryParameters = new URLSearchParams(window.top.location.search)
	const groupIdQueryParam = queryParameters.get("id")
	const [selectedMonths, setSelectedMonths] = useState<string[]>([])
	const groupCode = groupIdQueryParam && xrm.Page.data.entity.attributes.getByName("cw_groupcode").getValue()

	const {
		data: reportData,
		loading: loadingReportData,
		error: errorReportData
	} = useApiCall<any>(groupIdQueryParam ? `/getYearOverYearData?groupCode=${groupCode}` : `/getYearOverYearDataByEffDateMonths`, {
		method: groupIdQueryParam ? "GET" : "POST",
		body: groupIdQueryParam ? undefined : JSON.stringify({ months: selectedMonths }),
		refetchOnChanges: [groupCode, selectedMonths],
		canFetch: groupIdQueryParam ? !!groupCode : selectedMonths.length > 0
	})

	const getColumns = (): IColumn[] =>
		[
			selectedMonths.length > 0 && {
				key: "employer_name",
				name: "Name",
				fieldName: "employer_name",
				minWidth: 140,
				onRender: item => {
					return item.employer_name
				}
			},
			selectedMonths.length > 0 && {
				key: "client_code_2",
				name: "Group Code",
				fieldName: "client_code_2",
				minWidth: 60,
				onRender: item => {
					return item.client_code_2
				}
			},
			{
				key: "ty",
				name: "Treaty Year",
				fieldName: "ty",
				minWidth: 140,
				onRender: item => {
					return (
						<span>
							{item.ty} (
							{new Date(
								(() => {
									const dateStr = item.eff_date.endsWith("Z") ? item.eff_date : item.eff_date + "T00:00:00Z"
									const date = new Date(dateStr)
									date.setDate(date.getDate() + 1)
									return date
								})()
							).toLocaleString("en-US", { month: "short", year: "numeric" })}
							-{new Date(item.term_date.endsWith("Z") ? item.term_date : item.term_date + "T00:00:00Z").toLocaleString("en-US", { month: "short", year: "numeric" })})
						</span>
					)
				}
			},
			{
				key: "employer_sir",
				name: "Self Insured Retention (SIR)",
				fieldName: "employer_sir",
				minWidth: 90,
				onRender: item =>
					item.employer_sir?.toLocaleString("en-US", {
						style: "currency",
						currency: "USD"
					})
			},
			{
				key: "net_premiums",
				name: "Net Premiums",
				fieldName: "net_premiums",
				minWidth: 90,
				onRender: item =>
					item.net_premiums?.toLocaleString("en-US", {
						style: "currency",
						currency: "USD"
					})
			},
			{
				key: "prem_and_employer_fund",
				name: "Total Premiums and Employer Fund",
				fieldName: "prem_and_employer_fund",
				minWidth: 90,
				onRender: item =>
					item.prem_and_employer_fund?.toLocaleString("en-US", {
						style: "currency",
						currency: "USD"
					})
			},
			{
				key: "claim_amount",
				name: "Total Claims Payments",
				fieldName: "claim_amount",
				minWidth: 90,
				onRender: item =>
					item.claim_amount?.toLocaleString("en-US", {
						style: "currency",
						currency: "USD"
					})
			},
			{
				key: "prem_less_claims",
				name: "Premiums & Employer Fund Less Claims",
				fieldName: "prem_less_claims",
				minWidth: 90,
				onRender: item =>
					item.prem_less_claims?.toLocaleString("en-US", {
						style: "currency",
						currency: "USD"
					})
			},
			{
				key: "total_PL_percent",
				name: "Total P/L Margin",
				fieldName: "total_PL_percent",
				minWidth: 90,
				onRender: item => {
					const value = item.total_PL_percent
					if (value == null) return ""
					const percent = (value * 100).toFixed(2) + "%"
					const color = value >= 0 ? "green" : "red"
					return <span style={{ color }}>{percent}</span>
				}
			},
			{
				key: "employer_fund_balance",
				name: "Employer Fund Balance 50%",
				fieldName: "employer_fund_balance",
				minWidth: 90,
				onRender: item => {
					const halfValue = item.employer_fund_balance ? item.employer_fund_balance / 2 : 0
					return halfValue.toLocaleString("en-US", {
						style: "currency",
						currency: "USD"
					})
				}
			},
			{
				key: "aggregate_claims",
				name: "Aggregate Claims",
				fieldName: "aggregate_claims",
				minWidth: 90,
				onRender: item =>
					item.aggregate_claims?.toLocaleString("en-US", {
						style: "currency",
						currency: "USD"
					})
			},
			{
				key: "carrier_PL",
				name: "Carrier P/L",
				fieldName: "carrier_PL",
				minWidth: 90,
				onRender: item =>
					item.carrier_PL?.toLocaleString("en-US", {
						style: "currency",
						currency: "USD"
					})
			},
			{
				key: "PL_percent_gain_loss",
				name: "Carrier Margin",
				fieldName: "PL_percent_gain_loss",
				minWidth: 90,
				onRender: item => {
					const value = item.PL_percent_gain_loss
					if (value == null) return ""
					const percent = (value * 100).toFixed(2) + "%"
					const color = value >= 0 ? "green" : "red"
					return <span style={{ color }}>{percent}</span>
				}
			}
		].filter(Boolean)

	const items = reportData
		? [
				...reportData,
				{
					isTotalRow: true,
					...getColumns().reduce((acc, col) => {
						// Only sum numeric columns, skip non-numeric or calculated columns
						if (
							["employer_sir", "net_premiums", "prem_and_employer_fund", "claim_amount", "prem_less_claims", "employer_fund_balance", "aggregate_claims", "carrier_PL"].includes(col.key)
						) {
							acc[col.fieldName!] = reportData.reduce((sum, item) => {
								const value = Number(item[col.fieldName!]) || 0
								return sum + (col.key === "employer_fund_balance" ? value / 2 : value)
							}, 0)
						}
						// For percent columns, calculate the average (weighted or simple)
						if (["total_PL_percent", "PL_percent_gain_loss"].includes(col.key)) {
							const validItems = reportData.filter((item: Record<string, any>) => typeof item[col.key] === "number" && item[col.key] !== null && item[col.key] !== undefined)
							acc[col.key] =
								validItems.length > 0
									? validItems.reduce((sum: number, item: Record<string, any>) => sum + (typeof item[col.key] === "number" ? Number(item[col.key]) : 0), 0) / validItems.length
									: null
						}
						return acc
					}, {} as any),
					ty: "Total"
				}
			]
		: []

	const columns = getColumns().map(col => ({
		...col,
		onRender: (item: any) => {
			if (item.isTotalRow) {
				// For the "ty" column, show "Total"
				if (col.key === "ty") return <b>Total</b>
				// For numeric columns, show the sum
				if (["employer_sir", "net_premiums", "prem_and_employer_fund", "claim_amount", "prem_less_claims", "employer_fund_balance", "aggregate_claims", "carrier_PL"].includes(col.key)) {
					const value = item[col.fieldName!]
					return (
						<b>
							{value?.toLocaleString("en-US", {
								style: "currency",
								currency: "USD"
							})}
						</b>
					)
				}
				// For percent columns, show the average (as percent)
				if (["total_PL_percent", "PL_percent_gain_loss"].includes(col.key)) {
					const value = item[col.fieldName!]
					if (value == null) return <b>-</b>
					const percent = (value * 100).toFixed(2) + "%"
					const color = value >= 0 ? "green" : "red"
					return <b style={{ color }}>{percent}</b>
				}
				return null
			}
			return col.onRender ? col.onRender(item) : item[col.fieldName!]
		}
	}))

	const detailsListStyles: Partial<IDetailsListStyles> = {
		headerWrapper: {
			// This targets the header row
			".ms-DetailsHeader-cell": {
				whiteSpace: "normal",
				wordBreak: "break-word",
				lineHeight: "1.3",
				paddingTop: 8,
				paddingBottom: 8,
				height: 74
			},
			".ms-DetailsHeader": {
				height: 74
			}
		}
	}
	let groups = undefined
	if (selectedMonths.length > 0) {
		if (reportData && reportData.length > 0) {
			const groupMap: Record<string, number[]> = {}
			reportData.forEach((item: any, idx: number) => {
				const code = item.client_code_2 || "Unknown"
				if (!groupMap[code]) groupMap[code] = []
				groupMap[code].push(idx)
			})

			// Prepare new items array with group totals inserted after each group
			const newItems: any[] = []
			const newGroups: any[] = []
			let currentIndex = 0

			Object.entries(groupMap).forEach(([code, indices]) => {
				const groupItems = indices.map(idx => reportData[idx])
				const groupName = groupItems[0].employer_name + ` (${code})`

				// Add group items
				newItems.push(...groupItems)

				// Calculate group total row
				const groupTotalRow = {
					isTotalRow: true,
					...getColumns().reduce((acc, col) => {
						if (
							["employer_sir", "net_premiums", "prem_and_employer_fund", "claim_amount", "prem_less_claims", "employer_fund_balance", "aggregate_claims", "carrier_PL"].includes(col.key)
						) {
							acc[col.fieldName!] = groupItems.reduce((sum, item) => sum + (Number(item[col.fieldName!]) || 0), 0)
						}
						if (["total_PL_percent", "PL_percent_gain_loss"].includes(col.key)) {
							const validItems = groupItems.filter((item: Record<string, any>) => typeof item[col.key] === "number" && item[col.key] !== null && item[col.key] !== undefined)
							acc[col.key] =
								validItems.length > 0
									? validItems.reduce((sum: number, item: Record<string, any>) => sum + (typeof item[col.key] === "number" ? Number(item[col.key]) : 0), 0) / validItems.length
									: null
						}
						return acc
					}, {} as any),
					ty: "Total"
				}

				newItems.push(groupTotalRow)

				// Add group definition
				newGroups.push({
					key: code,
					name: groupName,
					startIndex: currentIndex,
					count: groupItems.length + 1 // group items + total row
				})

				currentIndex += groupItems.length + 1
			})

			items.splice(0, items.length, ...newItems)
			groups = newGroups
		}
	}

	const groupProps: IDetailsGroupRenderProps = {
		onRenderHeader: (props, defaultRender) => {
			if (!props) return null
			return <div style={{ backgroundColor: "#fff" }}>{defaultRender(props)}</div>
		}
	}

	return (
		<Stack tokens={{ childrenGap: 20 }} style={{ padding: 20 }}>
			{isUserInRole ? (
				<>
					{groupIdQueryParam && !groupCode && (
						<MessageBar messageBarType={MessageBarType.warning}>The current group does not have a valid group code. Please ensure the group is set up correctly in CRM.</MessageBar>
					)}
					{!groupIdQueryParam && (
						<Dropdown
							label="Select Group Cohort"
							options={Array.from({ length: 12 }, (_, i) => ({
								key: (i + 1).toString(),
								text: new Date(0, i).toLocaleString("en-US", { month: "long" })
							}))}
							multiSelect
							onChange={(_ev, option) => {
								if (option) {
									setSelectedMonths(prev => {
										if (prev.includes(option.key as string)) {
											return prev.filter(month => month !== option.key)
										} else {
											return [...prev, option.key as string]
										}
									})
								}
							}}
							placeholder="Select months"
							selectedKeys={selectedMonths}
							styles={{ root: { maxWidth: 300 } }}
						/>
					)}
					{loadingReportData && <ProgressIndicator label="Loading report data..." />}
					{errorReportData && <MessageBar messageBarType={MessageBarType.error}>Error loading report data: {errorReportData.message}</MessageBar>}
					{reportData && reportData.length > 0 && (
						<DetailsList
							groups={groups}
							items={items}
							selectionMode={SelectionMode.none}
							columns={columns}
							layoutMode={DetailsListLayoutMode.fixedColumns}
							styles={detailsListStyles}
							groupProps={groupProps}
						/>
					)}
					{reportData && reportData.length === 0 && (
						<MessageBar messageBarType={MessageBarType.info}>No year-over-year data available for the selected {groupIdQueryParam ? "group" : "months"}.</MessageBar>
					)}
				</>
			) : (
				<MessageBar messageBarType={MessageBarType.warning}>You do not have permission to view this report.</MessageBar>
			)}
		</Stack>
	)
}
